import React, { useState, useEffect } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, UserCog, Trash2, Edit2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ROLE_LABELS = {
  agency_admin: "Admin",
  agency_manager: "Manager",
  agency_member: "Member",
};

const ROLE_COLORS = {
  agency_admin: { bg: "#FEF3C7", text: "#D97706" },
  agency_manager: { bg: "#DBEAFE", text: "#1D4ED8" },
  agency_member: { bg: "#F5F3EF", text: "#57534E" },
};

export default function Team() {
  const { user: me } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editMember, setEditMember] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "agency_member", send_welcome_email: true });
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    api.get("/team").then((r) => setMembers(r.data)).finally(() => setLoading(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editMember) {
        const updates = { name: form.name, email: form.email, role: form.role };
        if (form.password) updates.password = form.password;
        const r = await api.put(`/team/${editMember.id}`, updates);
        setMembers((prev) => prev.map((m) => m.id === editMember.id ? r.data : m));
        toast.success("Team member updated");
      } else {
        const r = await api.post("/team", form);
        setMembers((prev) => [...prev, r.data]);
        toast.success("Team member added");
      }
      setShowDialog(false);
      setEditMember(null);
      setForm({ name: "", email: "", password: "", role: "agency_member", send_welcome_email: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Operation failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/team/${id}`);
      setMembers((prev) => prev.filter((m) => m.id !== id));
      toast.success("Member removed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove");
    } finally {
      setDeleteTarget(null);
    }
  };

  const openEdit = (m) => {
    setEditMember(m);
    setForm({ name: m.name, email: m.email, password: "", role: m.role, send_welcome_email: false });
    setShowDialog(true);
  };
  const openAdd = () => {
    setEditMember(null);
    setForm({ name: "", email: "", password: "", role: "agency_member", send_welcome_email: true });
    setShowDialog(true);
  };

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-8" data-testid="team-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl font-bold" style={{ color: "#292524" }}>Team</h1>
            <p className="text-sm mt-0.5" style={{ color: "#57534E" }}>{members.length} team member{members.length !== 1 ? "s" : ""}</p>
          </div>
          {me?.role === "agency_admin" && (
            <Button
              data-testid="add-team-member-button"
              onClick={openAdd}
              className="flex items-center gap-2"
              style={{ background: "#292524", color: "#fff" }}
            >
              <Plus size={15} strokeWidth={1.5} />
              Add Member
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading ? (
            <div className="col-span-3 text-center py-12 text-sm" style={{ color: "#A8A29E" }}>Loading...</div>
          ) : members.length === 0 ? (
            <div className="col-span-3 text-center py-12">
              <UserCog size={32} className="mx-auto mb-3" style={{ color: "#E7E5E4" }} />
              <p className="text-sm" style={{ color: "#A8A29E" }}>No team members yet</p>
            </div>
          ) : (
            members.map((member) => {
              const rc = ROLE_COLORS[member.role] || { bg: "#F5F3EF", text: "#57534E" };
              return (
                <div
                  key={member.id}
                  data-testid={`team-member-${member.id}`}
                  className="bg-white rounded-xl border p-5 cursor-pointer hover:shadow-md transition-shadow"
                  style={{ borderColor: "#E7E5E4" }}
                  onClick={() => openEdit(member)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold overflow-hidden"
                        style={{ background: member.avatar_image ? "transparent" : (member.avatar_color || "#D97706") }}
                      >
                        {member.avatar_image
                          ? <img src={member.avatar_image} alt="" className="w-full h-full object-cover" />
                          : member.name?.[0]?.toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium text-sm" style={{ color: "#292524" }}>{member.name}</p>
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: rc.bg, color: rc.text }}>
                          {ROLE_LABELS[member.role] || member.role}
                        </span>
                      </div>
                    </div>
                    {me?.role === "agency_admin" && member.id !== me.id && (
                      <button
                        data-testid={`delete-member-${member.id}`}
                        onClick={(e) => { e.stopPropagation(); setDeleteTarget(member); }}
                        className="p-1.5 rounded hover:bg-red-50"
                        style={{ color: "#C7C3BF" }}
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                  <p className="text-xs" style={{ color: "#A8A29E" }}>{member.email}</p>
                  {!member.is_active && (
                    <p className="text-xs mt-1" style={{ color: "#DC2626" }}>Inactive</p>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent data-testid="team-member-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif">{editMember ? "Edit Team Member" : "Add Team Member"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div><Label>Name *</Label><Input data-testid="team-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Email *</Label><Input data-testid="team-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>{editMember ? "New Password (optional)" : "Password *"}</Label><Input data-testid="team-password-input" type="password" required={!editMember} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
            <div>
              <Label>Role</Label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "#E7E5E4", background: "#fff" }}>
                <option value="agency_member">Member</option>
                <option value="agency_manager">Manager</option>
                <option value="agency_admin">Admin</option>
              </select>
            </div>
            {!editMember && (
              <div className="flex items-center gap-2 pt-1">
                <input
                  data-testid="team-notify-checkbox"
                  type="checkbox"
                  id="team-notify"
                  checked={form.send_welcome_email}
                  onChange={(e) => setForm({ ...form, send_welcome_email: e.target.checked })}
                  className="h-4 w-4 rounded"
                />
                <label htmlFor="team-notify" className="text-sm cursor-pointer" style={{ color: "#57534E" }}>
                  Send welcome email with login details
                </label>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button data-testid="save-team-member-button" type="submit" disabled={saving} style={{ background: "#292524", color: "#fff" }}>
                {saving ? "Saving..." : editMember ? "Update" : "Add Member"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-member-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Team Member</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove <strong>{deleteTarget?.name}</strong> from the team? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-member-confirm"
              onClick={() => handleDelete(deleteTarget?.id)}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
